import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useUnmount } from 'react-use';
import { useDebouncedCallback } from 'use-debounce';

import { useTrackAgentMessageMissed } from '@air/analytics';
import { Agents } from '@air/api';
import { type AgentSessionMessageResponse } from '@air/server-sdk';
import { THREE_SECONDS } from '@air/utilities';
import { callAirApi } from '@air/utils-api';

import { getPrivateWorkspaceAgentSessionMessagesInfiniteKey } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionMessagesInfinite';
import {
  mergeWorkspaceAgentCatchUpMessagesIntoFirstPage,
  WORKSPACE_AGENT_LIVE_MESSAGE_CATCH_UP_LIMIT,
  type WorkspaceAgentSessionMessagesInfiniteData,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentMessageCatchUp';

export type UsePrivateWorkspaceAgentMessageCatchUpParams = {
  getActiveSessionId: () => string | null;
  /** Consent-dock catch-up still gap-fills the store; analytics must not count those rows as missed. */
  getHasPendingApproval: () => boolean;
  mergeServerMessages: (serverMessages: AgentSessionMessageResponse[]) => AgentSessionMessageResponse[];
  workspaceId: string;
};

export type UsePrivateWorkspaceAgentMessageCatchUpResult = {
  cancelLiveSessionCatchUp: () => void;
  refreshLiveSessionMessages: () => Promise<void>;
  scheduleLiveSessionCatchUp: (sessionId: string) => void;
};

/**
 * Option B (WORK-448): after WSS applies the payload, debounce a latest-page list for the open
 * session and merge anything the socket missed.
 */
export const usePrivateWorkspaceAgentMessageCatchUp = ({
  getActiveSessionId,
  getHasPendingApproval,
  mergeServerMessages,
  workspaceId,
}: UsePrivateWorkspaceAgentMessageCatchUpParams): UsePrivateWorkspaceAgentMessageCatchUpResult => {
  const queryClient = useQueryClient();
  const { trackAgentMessageMissed } = useTrackAgentMessageMissed();

  const fetchOpenSessionLatestMessages = useCallback(
    async (openSessionId: string) => {
      if (getActiveSessionId() !== openSessionId) {
        return;
      }

      try {
        const response = await callAirApi(() =>
          Agents.Sessions.Messages.list({
            agentType: 'workspace',
            limit: WORKSPACE_AGENT_LIVE_MESSAGE_CATCH_UP_LIMIT,
            sessionId: openSessionId,
            workspaceId,
          }),
        );

        if (getActiveSessionId() !== openSessionId) {
          return;
        }

        const missedMessages = mergeServerMessages(response.data);

        if (!getHasPendingApproval()) {
          for (const message of missedMessages) {
            trackAgentMessageMissed({
              message_id: message.id,
              session_id: message.sessionId,
            });
          }
        }

        queryClient.setQueryData<WorkspaceAgentSessionMessagesInfiniteData>(
          getPrivateWorkspaceAgentSessionMessagesInfiniteKey({ sessionId: openSessionId, workspaceId }),
          (current) =>
            mergeWorkspaceAgentCatchUpMessagesIntoFirstPage({
              current,
              latestMessages: response.data,
            }),
        );
      } catch {
        // Best-effort gap-fill — a failed list must not become a chat error for a message that did send.
      }
    },
    [getActiveSessionId, getHasPendingApproval, mergeServerMessages, queryClient, trackAgentMessageMissed, workspaceId],
  );

  // Debounce only the open session — workspace-wide WSS handlers pass every session id, but a
  // single trailing debounce would let a later background/teammate event reset the timer and skip
  // the open thread's catch-up entirely. Non-open sessions are ignored at schedule time.
  const [debouncedFetchOpenSessionCatchUp, cancelLiveSessionCatchUp] = useDebouncedCallback(() => {
    const openSessionId = getActiveSessionId();

    if (openSessionId === null) {
      return;
    }

    void fetchOpenSessionLatestMessages(openSessionId);
  }, THREE_SECONDS);

  const scheduleLiveSessionCatchUp = useCallback(
    (eventSessionId: string) => {
      const openSessionId = getActiveSessionId();

      if (openSessionId === null || openSessionId !== eventSessionId) {
        return;
      }

      debouncedFetchOpenSessionCatchUp();
    },
    [debouncedFetchOpenSessionCatchUp, getActiveSessionId],
  );

  const refreshLiveSessionMessages = useCallback(async () => {
    const openSessionId = getActiveSessionId();

    if (openSessionId === null) {
      return;
    }

    // Stall recovery cannot wait for the WSS 3s debounce — the socket may already be silent.
    await fetchOpenSessionLatestMessages(openSessionId);
  }, [fetchOpenSessionLatestMessages, getActiveSessionId]);

  // A pending timer must not fire after this tree is gone. Opening a session hydrates from the
  // messages infinite query, so there is nothing to merge here.
  useUnmount(() => {
    cancelLiveSessionCatchUp();
  });

  return {
    cancelLiveSessionCatchUp,
    refreshLiveSessionMessages,
    scheduleLiveSessionCatchUp,
  };
};
